import { z } from "zod"
import {
    buildingHeightOptions,
    canBeHomeRepairedOptions,
    heightOfWaterOptions,
    isNewHousingHelpNeededOptions,
    kindOfRoofWorkOptions,
    workScopeStatusOptions,
    assessmentCompletedOptions,
    potentialStructuralProblemsOptions,
    residenceTypeOptions,
    roofPitchOptions,
    structureTypeOptions,
    isHomeRepairedOptions
} from "../../constants/options"
import {
    phoneSchema,
    selectSchema,
    textSchema,
    claimedBySchema,
} from "../../../../shared/lib/schemas/common.schema"
import { makeOptional } from "../../../../shared/lib/helpers/schema.helpers"
import { isUnknownLocationValue } from "../../lib/unknown-location"
import { completionDateSchema } from "./completion-date.schema"

export const repairabilityInfoFormSchema = z.object({
    assessmentCompleted: makeOptional(selectSchema(assessmentCompletedOptions)),

    assessorName: makeOptional(textSchema()),

    residenceType: makeOptional(selectSchema(residenceTypeOptions)),

    structureType: makeOptional(selectSchema(structureTypeOptions)),

    ownerNameIfRental: makeOptional(textSchema()),

    ownerPhoneNumberIfRental: makeOptional(phoneSchema),

    canBeRepaired: makeOptional(selectSchema(isHomeRepairedOptions)),

    newHousingHelpNeeded: makeOptional(
        selectSchema(isNewHousingHelpNeededOptions)
    ),

    rebuildingHelpNeeded: makeOptional(z.string()),

    // permissionToWork: makeOptional(z.string()),
    
    rebuildingSuppliesHelpNeeded: makeOptional(z.string()),

    heightOfWater: makeOptional(selectSchema(heightOfWaterOptions)),

    buildingHeight: makeOptional(selectSchema(buildingHeightOptions)),

    affectedSquareFeet: makeOptional(z.number(), "number"),

    potentialStructuralProblems: makeOptional(
        selectSchema(potentialStructuralProblemsOptions)
    ),

    structuralProblemDescription: makeOptional(
        z.string( )
            .trim()
            .max(2000, "Cannot exceed 2,000 characters")
            .optional()
    ),

   
})

export const treeDebrisInfoFormSchema = z.object({
    treeWorkNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    treeWorkNeededDateCompleted: completionDateSchema,
    treeClaimedBy: claimedBySchema,
    exteriorDebrisRemovalNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    exteriorDebrisRemovalNeededDateCompleted: completionDateSchema,
    debrisClaimedBy: claimedBySchema,
    heavyEquipmentNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    heavyEquipmentNeededDateCompleted: completionDateSchema,
    heavyClaimedBy: claimedBySchema,
    description: makeOptional(textSchema("lg")),
    // Goodwill: toggle + status/claimed by per workscope
    goodwillTreeWorkOn: makeOptional(z.boolean()),
    goodwillTreeWorkNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillTreeWorkNeededClaimedBy: claimedBySchema.optional(),
    goodwillExteriorDebrisOn: makeOptional(z.boolean()),
    goodwillExteriorDebrisRemovalNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillExteriorDebrisRemovalNeededClaimedBy: claimedBySchema.optional(),
    goodwillHeavyEquipmentOn: makeOptional(z.boolean()),
    goodwillHeavyEquipmentNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillHeavyEquipmentNeededClaimedBy: claimedBySchema.optional(),
})

export const roofingInfoFormSchema = z.object({
    roofing: makeOptional(selectSchema(workScopeStatusOptions)),
    roofingDateCompleted: completionDateSchema,
    roofingClaimedBy: claimedBySchema,
    kindOfRoofWork: makeOptional(selectSchema(kindOfRoofWorkOptions)),
    roofTarpNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    roofTarpNeededDateCompleted: completionDateSchema,
    roofTarpClaimedBy: claimedBySchema,
    roofPitch: makeOptional(selectSchema(roofPitchOptions)),
    description: makeOptional(textSchema("lg")),
    goodwillRoofingOn: makeOptional(z.boolean()),
    goodwillRoofing: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillRoofingClaimedBy: claimedBySchema.optional(),
    goodwillRoofTarpOn: makeOptional(z.boolean()),
    goodwillRoofTarpNeeded: makeOptional(selectSchema(workScopeStatusOptions)),
    goodwillRoofTarpNeededClaimedBy: claimedBySchema.optional(),
})

const homeForm1BaseSchema = z.object({
    address: textSchema("lg"),
    city: makeOptional(z.string()),
    state: makeOptional(z.string()),
    // zipCode: makeOptional(z.string()),
    zipCode: makeOptional(z.coerce.string()),
    longitude: makeOptional(z.number(), "number"),
    latitude: makeOptional(z.number(), "number"),
    region: makeOptional(z.string()),
    county: makeOptional(z.string()),
    dogsInHome: makeOptional(z.string()),
    shedNeeded: makeOptional(z.string()),
    hasFloodInsurance: z.string().nullable().optional(),
    hasHomeInsurance: z.string().nullable().optional(),
    googlePicLink: makeOptional(z.string()),
    projectGeneralNotes: makeOptional(
        z.string()
            .trim()
            .max(30000, "Cannot exceed 30,000 characters")
            .optional()
    ),
    isReported: makeOptional(z.boolean()),
    repairabilityInfo: repairabilityInfoFormSchema.nullable().optional(),
    treeDebrisInfo: treeDebrisInfoFormSchema.nullable().optional(),
    roofingInfo: roofingInfoFormSchema.nullable().optional(),
})

export const homeForm1Schema = homeForm1BaseSchema.refine((data) => {
    if (isUnknownLocationValue(data.address)) {
        return true
    }
    // If address is provided, city and state must also be provided
    if (data.address && data.address.trim() !== "") {
        return data.city && data.city.trim() !== "" && data.state && data.state.trim() !== "";
    }
    return true;
}, {
    message:
        "Either select a validated address from the dropdown or use the 'I don't have a street address' link to generate valid gps coordinates",
    path: ["address"], // This will show the error on the address field
})

export type HomeForm1Schema = z.infer<typeof homeForm1Schema>
export { homeForm1BaseSchema }
